"use client"

import { useEffect, useRef } from "react"
import { motion, useAnimation, useInView } from "framer-motion"
import { ArrowRight, MapPin, Phone, Mail, ChevronDown, Check, Award, Shield, Gem } from "lucide-react"
import Footer from "./Footer"
import { Link } from "react-router-dom"
import Image1 from "../Assets/b715b1c65ddd018e92d96ff0ed30cbbd.jpg"

// Rough Emeralds Videos
const Video1 = "/Vedios/IMG_2457.MP4"
const Video2 = "/Vedios/IMG_2461.MP4"
const Video3 = "/Vedios/WhatsApp Video 2025-04-29 at 1.00.28 PM.mp4"

// Cutted Emeralds
import Image5 from "../Assets/CuttedEmeralds/cut1.jpg"
import Image6 from "../Assets/CuttedEmeralds/cut2.jpg"
import Image7 from "../Assets/CuttedEmeralds/cut3.jpg"
import Image8 from "../Assets/4k-Raw-Emerald-Graphic-47767139-1.png"

// Animation variants
const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } },
}

const fadeInRight = {
  hidden: { opacity: 0, x: -50 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.8, ease: "easeOut" } },
}

const fadeInLeft = {
  hidden: { opacity: 0, x: 50 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.8, ease: "easeOut" } },
}

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.3,
    },
  },
}

const emeraldTypes = [
  {
    id: 1,
    name: "Rough Panjshir Emerald",
    description: "Uncut Panjshir treasures with vivid green brilliance, hand-selected by Dubai Panjshir EmeraldVault for unrivaled allure and investment potential.",
    video: Video1,
    href: "/shop-roughemerald",
  },
  {
    id: 2,
    name: "Rough Panjshir Emerald",
    description: "Raw Panjshir emeralds with intense chromium-rich hues, curated for visionaries seeking timeless wealth and elegance.",
    video: Video2,
    href: "/shop-roughemerald",
  },
  {
    id: 3,
    name: "Rough Panjshir Emerald",
    description: "Distinctive Panjshir gems with captivating inclusions, embodying nature’s artistry, chosen for Dubai Panjshir EmeraldVault investors.",
    video: Video3,
    href: "/shop-roughemerald",
  },
  {
    id: 4,
    name: "Cut Panjshir Emerald (Classic)",
    description: "Flawlessly faceted Panjshir emeralds with radiant VVS clarity, crafted for eternal sophistication and soaring value.",
    image: `${Image5}`,
    href: "/shop-cutemerald",
  },
  {
    id: 5,
    name: "Cut Panjshir Emerald (Modern)",
    description: "Precision-cut Panjshir gems with dazzling transparency, shaped for modern luxury and exponential growth.",
    image: `${Image6}`,
    href: "/shop-cutemerald",
  },
  {
    id: 6,
    name: "Cut Panjshir Emerald (Fancy)",
    description: "Artistically faceted Panjshir emeralds in unique shapes, bursting with vibrant color for bold investment.",
    image: `${Image7}`,
    href: "/shop-cutemerald",
  },
]

const whyChooseUs = [
  {
    id: 1,
    title: "Unparalleled Excellence",
    description: "Our Panjshir emeralds, certified by elite labs, dazzle with unmatched chromium-infused brilliance, securing your legacy.",
    icon: <Award className="h-8 w-8 text-emerald-400" />,
    color: "from-emerald-500 to-emerald-600",
  },
  {
    id: 2,
    title: "Ethical Mastery",
    description: "Ethically sourced Panjshir emeralds reflect Dubai Panjshir EmeraldVault’s commitment to sustainable luxury and social impact.",
    icon: <Shield className="h-8 w-8 text-emerald-400" />,
    color: "from-green-500 to-green-600",
  },
  {
    id: 3,
    title: "Artisan Precision",
    description: "Master decorators sculpt Panjshir emeralds to amplify their No. 1 green radiance, crafted for visionary investors.",
    icon: <Gem className="h-8 w-8 text-emerald-400" />,
    color: "from-teal-500 to-teal-600",
  },
  {
    id: 4,
    title: "Timeless Assurance",
    description: "Every Panjshir emerald comes with Dubai Panjshir EmeraldVault’s lifetime authenticity guarantee and complimentary maintenance.",
    icon: <Phone className="h-8 w-8 text-emerald-400" />,
    color: "from-emerald-600 to-emerald-700",
    phone: "020 220 1616",
  },
]

export default function HomePage() {
  const heroRef = useRef(null)
  const roughEmeraldsRef = useRef(null)
  const cutEmeraldsRef = useRef(null)
  const stakeHoldingRef = useRef(null)
  const whyChooseUsRef = useRef(null)
  const infoRef = useRef(null)

  const heroInView = useInView(heroRef, { once: true, amount: 0.3 })
  const roughEmeraldsInView = useInView(roughEmeraldsRef, { once: true, amount: 0.3 })
  const cutEmeraldsInView = useInView(cutEmeraldsRef, { once: true, amount: 0.3 })
  const stakeHoldingInView = useInView(stakeHoldingRef, { once: true, amount: 0.3 })
  const whyChooseUsInView = useInView(whyChooseUsRef, { once: true, amount: 0.3 })
  const infoInView = useInView(infoRef, { once: true, amount: 0.3 })

  const heroControls = useAnimation()
  const roughEmeraldsControls = useAnimation()
  const cutEmeraldsControls = useAnimation()
  const stakeHoldingControls = useAnimation()
  const whyChooseUsControls = useAnimation()
  const infoControls = useAnimation()

  useEffect(() => {
    if (heroInView) heroControls.start("visible")
    if (roughEmeraldsInView) roughEmeraldsControls.start("visible")
    if (cutEmeraldsInView) cutEmeraldsControls.start("visible")
    if (stakeHoldingInView) stakeHoldingControls.start("visible")
    if (whyChooseUsInView) whyChooseUsControls.start("visible")
    if (infoInView) infoControls.start("visible")
  }, [
    heroInView,
    roughEmeraldsInView,
    cutEmeraldsInView,
    stakeHoldingInView,
    whyChooseUsInView,
    infoInView,
    heroControls,
    roughEmeraldsControls,
    cutEmeraldsControls,
    stakeHoldingControls,
    whyChooseUsControls,
    infoControls,
  ])

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-b from-gray-50 to-white font-serif overflow-x-hidden">
      {/* Hero Section */}
      <motion.section
        ref={heroRef}
        initial="hidden"
        animate={heroControls}
        variants={fadeIn}
        className="relative h-[95vh] flex items-center justify-center overflow-hidden"
      >
        <div className="absolute inset-0 z-0">
          <img
            src={Image1 || "/placeholder.svg"}
            alt="Dubai Panjshir EmeraldVault Hero"
            className="w-full h-full object-cover brightness-75"
            style={{ objectFit: "cover", backgroundRepeat: "no-repeat" }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-950/50 via-emerald-900/30 to-transparent" />
        </div>

        <div className="container relative z-10 px-4 sm:px-6 max-w-7xl flex flex-col items-start text-white">
          <motion.h1
            className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 tracking-tight"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            Unveil the Majesty of <span className="text-emerald-300 italic">Dubai Panjshir EmeraldVault</span>
          </motion.h1>

          <motion.p
            className="text-sm md:text-xl max-w-xl mb-8 text-emerald-50/90 leading-relaxed"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            Step into a world of opulence with Dubai Panjshir EmeraldVault’s Panjshir emeralds, the world’s No. 1 gems. Radiant, chromium-rich greens outshine all, offering rare beauty and soaring value beyond $10,000 per carat. Ethically sourced and poised for exponential growth, seize your legacy of wealth today!
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="flex flex-col sm:flex-row gap-4"
          >
            <div className="relative group">
              <button className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 h-11 px-8 bg-emerald-600 hover:bg-emerald-700 text-white border border-emerald-500/50 shadow-lg shadow-emerald-900/20">
                Discover Our Gems
                <ChevronDown className="ml-2 h-4 w-4" />
              </button>
              <div className="absolute left-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                <div className="py-1" role="menu" aria-orientation="vertical">
                  <Link
                    to="/shop-roughemerald"
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-emerald-50 hover:text-emerald-700"
                    role="menuitem"
                  >
                    Rough Panjshir Emerald
                  </Link>
                  <Link
                    to="/shop-cutemerald"
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-emerald-50 hover:text-emerald-700"
                    role="menuitem"
                  >
                    Cut Panjshir Emerald
                  </Link>
                </div>
              </div>
            </div>
            <button className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 h-11 px-8 border border-emerald-400/50 text-white hover:bg-emerald-800/30 backdrop-blur-sm">
              <Link to="/pricing-plans">Join Our Stakeholders</Link>
            </button>
          </motion.div>
        </div>

        <motion.div
          className="absolute bottom-10 left-1/2 transform -translate-x-1/2"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            duration: 1.5,
            delay: 1.5,
            repeat: Number.POSITIVE_INFINITY,
            repeatType: "reverse",
            ease: "easeInOut",
          }}
        >
          <div className="flex flex-col items-center">
            <span className="text-white/70 text-sm mb-2 font-sans">Dive Into Luxury</span>
            <ChevronDown className="w-8 h-8 text-emerald-300" />
          </div>
        </motion.div>

        <motion.div
          className="absolute top-1/4 right-[5%] w-32 h-32 rounded-full bg-emerald-500/10"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1, delay: 1.2 }}
        />
        <motion.div
          className="absolute bottom-1/4 left-[10%] w-24 h-24 rounded-full bg-emerald-600/10"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1, delay: 1.4 }}
        />
      </motion.section>

      {/* Rough Emeralds Section */}
      <section className="py-24 px-4 sm:px-6">
        <motion.div
          ref={roughEmeraldsRef}
          initial="hidden"
          animate={roughEmeraldsControls}
          variants={fadeIn}
          className="container mx-auto px-4 sm:px-6 max-w-7xl"
        >
          <div className="text-center mb-16">
            <motion.h2 variants={fadeIn} className="text-4xl md:text-5xl font-bold mb-4 text-gray-900">
              Raw Panjshir Emerald Splendor
            </motion.h2>
            <motion.p variants={fadeIn} className="max-w-2xl mx-auto text-gray-600 text-lg">
              Behold the untamed beauty of Dubai Panjshir EmeraldVault’s rough Panjshir emeralds—the world’s No. 1 gems. Handpicked for their radiant chromium-rich allure, these rare treasures promise timeless elegance and soaring value for visionary investors.
            </motion.p>
          </div>

          <motion.div variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {emeraldTypes.slice(0, 3).map((emerald) => (
              <motion.div key={emerald.id} variants={fadeIn} whileHover={{ y: -10, transition: { duration: 0.3 } }}>
                <div className="rounded-lg border bg-card text-card-foreground shadow-sm overflow-hidden h-full transition-all duration-300 hover:shadow-2xl border-emerald-100 group">
                  <div className="relative h-72">
                    <video
                      src={emerald.video || "/placeholder.mp4"}
                      autoPlay
                      loop
                      muted
                      playsInline
                      title={emerald.name}
                      onEnded={(e) => e.target.play()}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </div>
                  <div className="p-6 bg-gradient-to-b from-white to-emerald-50">
                    <h3 className="text-xl font-semibold mb-2 text-gray-900">{emerald.name}</h3>
                    <p className="text-gray-600 mb-4">{emerald.description}</p>
                    <button className="p-0 mt-2 text-emerald-700 hover:text-emerald-900 group flex items-center underline-offset-4 hover:underline">
                      <Link to={emerald.href}>Explore Gem</Link>
                      <ArrowRight className="ml-2 h-4 w-4 transform group-hover:translate-x-1 transition-transform" />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </section>

      {/* Cut Emeralds Section */}
      <section className="py-24 px-4 sm:px-6 bg-gradient-to-b from-emerald-50 to-white">
        <motion.div
          ref={cutEmeraldsRef}
          initial="hidden"
          animate={cutEmeraldsControls}
          variants={fadeIn}
          className="container mx-auto px-4 sm:px-6 max-w-7xl"
        >
          <div className="text-center mb-16">
            <motion.h2 variants={fadeIn} className="text-4xl md:text-5xl font-bold mb-4 text-gray-900">
              Faceted Panjshir Emerald Perfection
            </motion.h2>
            <motion.p variants={fadeIn} className="max-w-2xl mx-auto text-gray-600 text-lg">
              Experience the pinnacle of craftsmanship with Dubai Panjshir EmeraldVault’s cut Panjshir emeralds, the No. 1 gems sculpted to unleash vibrant green fire, offering timeless elegance and unmatched investment potential.
            </motion.p>
          </div>

          <motion.div variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {emeraldTypes.slice(3, 6).map((emerald) => (
              <motion.div key={emerald.id} variants={fadeIn} whileHover={{ y: -10, transition: { duration: 0.3 } }}>
                <div className="rounded-lg border bg-card text-card-foreground shadow-sm overflow-hidden h-full transition-all duration-300 hover:shadow-2xl border-emerald-100 group">
                  <div className="relative h-72">
                    <img
                      src={emerald.image || "/placeholder.svg"}
                      alt={emerald.name}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </div>
                  <div className="p-6 bg-gradient-to-b from-white to-emerald-50">
                    <h3 className="text-xl font-semibold mb-2 text-gray-900">{emerald.name}</h3>
                    <p className="text-gray-600 mb-4">{emerald.description}</p>
                    <button className="p-0 mt-2 text-emerald-700 hover:text-emerald-900 group flex items-center underline-offset-4 hover:underline">
                      <Link to={emerald.href}>Explore Gem</Link>
                      <ArrowRight className="ml-2 h-4 w-4 transform group-hover:translate-x-1 transition-transform" />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </section>

      {/* Stake Holding Section */}
      <section className="py-24 px-4 sm:px-6 bg-white text-gray-900">
        <motion.div
          ref={stakeHoldingRef}
          initial="hidden"
          animate={stakeHoldingControls}
          variants={fadeIn}
          className="container mx-auto px-4 sm:px-6 max-w-7xl"
        >
          <div className="text-center mb-16">
            <motion.div
              variants={fadeIn}
              className="inline-block px-4 py-1 bg-emerald-100 text-emerald-700 rounded-full font-sans text-sm tracking-wider uppercase mb-4"
            >
              Your Path to Wealth
            </motion.div>
            <motion.h2 variants={fadeIn} className="text-4xl md:text-5xl font-bold mb-4 text-gray-900">
              Become a Panjshir Emerald Stakeholder
            </motion.h2>
            <motion.p variants={fadeIn} className="max-w-2xl mx-auto text-gray-600 text-lg">
              Unlock a legacy of prosperity with Dubai Panjshir EmeraldVault’s stake holding program. Invest in the world’s No. 1 Panjshir emeralds, ethically sourced and soaring beyond $10,000 per carat, for unrivaled wealth growth.
            </motion.p>
          </div>

          <motion.div variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div variants={fadeIn} whileHover={{ y: -10, transition: { duration: 0.3 } }} className="relative group">
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-emerald-100 to-emerald-200 transform group-hover:scale-[1.03] transition-transform duration-300 -z-10"></div>
              <div className="relative p-8 rounded-2xl bg-white border border-emerald-200 shadow-lg h-full">
                <div className="bg-gradient-to-br from-emerald-400 to-emerald-600 p-3 rounded-xl inline-block mb-6 shadow-md">
                  <Gem className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-3 text-gray-900">High-Yield Investment</h3>
                <p className="text-gray-600">
                  Stake in Panjshir emeralds with proven value growth, offering returns that outpace traditional investments—premium gems exceed $10,000 per carat.
                </p>
                <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-emerald-400 to-gold-400 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 versa left rounded-b-2xl"></div>
              </div>
            </motion.div>

            <motion.div variants={fadeIn} whileHover={{ y: -10, transition: { duration: 0.3 } }} className="relative group">
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-emerald-100 to-emerald-200 transform group-hover:scale-[1.03] transition-transform duration-300 -z-10"></div>
              <div className="relative p-8 rounded-2xl bg-white border border-emerald-200 shadow-lg h-full">
                <div className="bg-gradient-to-br from-emerald-400 to-emerald-600 p-3 rounded-xl inline-block mb-6 shadow-md">
                  <Shield className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-3 text-gray-900">Secure & Transparent</h3>
                <p className="text-gray-600">
                  Your stake in Panjshir emeralds is backed by certified gems, with full transparency and secure storage in Dubai’s elite vaults.
                </p>
                <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-emerald-400 to-gold-400 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 versa left rounded-b-2xl"></div>
              </div>
            </motion.div>

            <motion.div variants={fadeIn} whileHover={{ y: -10, transition: { duration: 0.3 } }} className="relative group">
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-emerald-100 to-emerald-200 transform group-hover:scale-[1.03] transition-transform duration-300 -z-10"></div>
              <div className="relative p-8 rounded-2xl bg-white border border-emerald-200 shadow-lg h-full">
                <div className="bg-gradient-to-br from-emerald-400 to-emerald-600 p-3 rounded-xl inline-block mb-6 shadow-md">
                  <Gem className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-3 text-gray-900">Elite Privileges</h3>
                <p className="text-gray-600">
                  Enjoy VIP access to exclusive Panjshir emerald releases, private auctions, and personalized consultations with Dubai Panjshir EmeraldVault.
                </p>
                <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-emerald-400 to-gold-400 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 versa left rounded-b-2xl"></div>
              </div>
            </motion.div>
          </motion.div>

          <motion.div variants={fadeIn} className="mt-16 text-center">
            <Link to="/pricing-plans">
              <button className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 h-12 px-10 bg-gradient-to-r from-emerald-500 to-gold-500 hover:from-emerald-600 hover:to-gold-600 text-white shadow-lg shadow-emerald-500/30 transform hover:scale-105 transition-all duration-300">
                Secure Your Stake Now
                <ArrowRight className="ml-2 h-5 w-5" />
              </button>
            </Link>
          </motion.div>
        </motion.div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-24 px-4 sm:px-6 bg-gradient-to-r from-emerald-900 to-green-900 text-white">
        <motion.div
          ref={whyChooseUsRef}
          initial="hidden"
          animate={whyChooseUsControls}
          variants={fadeIn}
          className="container mx-auto px-4 sm:px-6 max-w-7xl"
        >
          <div className="text-center mb-16">
            <motion.div
              variants={fadeIn}
              className="inline-block px-4 py-1 bg-emerald-800 rounded-full text-emerald-200 font-sans text-sm tracking-wider uppercase mb-4"
            >
              Our Legacy
            </motion.div>
            <motion.h2 variants={fadeIn} className="text-4xl md:text-5xl font-bold mb-4">
              Why Dubai Panjshir EmeraldVault Shines
            </motion.h2>
            <motion.p variants={fadeIn} className="max-w-2xl mx-auto text-emerald-100 text-lg">
              At Dubai Panjshir EmeraldVault, we redefine luxury with No. 1 Panjshir emeralds, ethically sourced and masterfully crafted to inspire wealth and legacy for visionary investors.
            </motion.p>
          </div>

          <motion.div variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChooseUs.map((item) => (
              <motion.div
                key={item.id}
                variants={fadeIn}
                whileHover={{ y: -10, transition: { duration: 0.3 } }}
                className="relative group"
              >
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-emerald-600/50 to-emerald-800/50 transform group-hover:scale-[1.03] transition-transform duration-300 -z-10"></div>
                <div className="relative p-8 rounded-2xl backdrop-blur-sm border border-emerald-600/20 h-full">
                  <div className="bg-gradient-to-br from-emerald-700 to-emerald-900 p-3 rounded-xl inline-block mb-6 shadow-lg">
                    {item.icon}
                  </div>
                  <h3 className="text-xl font-bold mb-3">{item.title}</h3>
                  <p className="text-emerald-100">{item.description}</p>
                  {item.phone && (
                    <a href={`tel:${item.phone}`} className="mt-4 inline-block text-emerald-300 hover:text-emerald-100">
                      Call us: {item.phone}
                    </a>
                  )}
                  <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-emerald-400 to-green-400 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 versa left rounded-b-2xl"></div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </section>

      {/* Information Section */}
      <section className="py-24 px-4 sm:px-6 bg-gradient-to-b from-emerald-50 to-white">
        <motion.div
          ref={infoRef}
          initial="hidden"
          animate={infoControls}
          variants={fadeIn}
          className="container mx-auto px-4 sm:px-6 max-w-7xl"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div variants={fadeInRight}>
              <div className="inline-block px-4 py-1 bg-emerald-100 rounded-full text-emerald-800 font-sans text-sm tracking-wider uppercase mb-4">
                Our Promise
              </div>
              <h2 className="text-4xl md:text-5xl font-bold mb-8 text-gray-900">The Dubai Panjshir EmeraldVault Legacy</h2>
              <div className="space-y-8">
                <div className="flex items-start">
                  <div className="flex-shrink-0 h-12 w-12 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-700 flex items-center justify-center mr-5 shadow-lg">
                    <Check className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2 text-gray-900">Ethical Elegance</h3>
                    <p className="text-gray-600 leading-relaxed">
                      Our Panjshir emeralds are ethically sourced from Afghanistan’s Panjshir Valley, supporting communities and offering investors sustainable luxury.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex-shrink-0 h-12 w-12 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-700 flex items-center justify-center mr-5 shadow-lg">
                    <Award className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2 text-gray-900">Certified Brilliance</h3>
                    <p className="text-gray-600 leading-relaxed">
                      Every Panjshir emerald, the world’s No. 1 gem, is certified by premier labs, ensuring unmatched quality and investment value.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex-shrink-0 h-12 w-12 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-700 flex items-center justify-center mr-5 shadow-lg">
                    <Gem className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2 text-gray-900">Bespoke Creations</h3>
                    <p className="text-gray-600 leading-relaxed">
                      Craft your legacy with custom Panjshir emerald jewelry, designed by Dubai Panjshir EmeraldVault artisans to amplify wealth and beauty.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div variants={fadeInLeft} className="relative">
              <div className="relative h-[600px] rounded-2xl overflow-hidden shadow-2xl">
                <img src={`${Image8}`} alt="Dubai Panjshir EmeraldVault Expert" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-emerald-900/70 to-transparent"></div>
                <div className="absolute bottom-8 left-8 right-8 text-white">
                  <h3 className="text-2xl font-bold mb-2">Masterful Craftsmanship</h3>
                  <p className="text-emerald-100">
                    Our artisans transform No. 1 Panjshir emeralds into timeless treasures, crafted for investors seeking beauty and growth.
                  </p>
                </div>
              </div>
              <div className="absolute -bottom-8 -left-8 w-64 h-64 bg-emerald-100 rounded-2xl -z-10"></div>
              <div className="absolute -top-8 -right-8 w-40 h-40 bg-emerald-200 rounded-2xl -z-10"></div>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* Contact Section */}
      <section className="py-24 px-4 sm:px-6 bg-gradient-to-r from-emerald-900 to-green-900 text-white">
        <div className="container mx-auto px-4 sm:px-6 max-w-7xl">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-1 bg-emerald-800 rounded-full text-emerald-200 font-sans text-sm tracking-wider uppercase mb-4">
              Connect With Us
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Your Journey Begins Here</h2>
            <p className="max-w-2xl mx-auto opacity-90 text-lg">
              Intrigued by the allure of our Panjshir emeralds? Reach out to Dubai Panjshir EmeraldVault for bespoke guidance and unparalleled service.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <a
              href="tel:0202201616"
              className="flex flex-col items-center text-center p-8 bg-emerald-800/50 backdrop-blur-sm rounded-2xl border border-emerald-700/50 hover:transform hover:scale-105 transition-transform duration-300"
            >
              <Phone className="h-12 w-12 mb-6 text-emerald-300" />
              <h3 className="text-xl font-semibold mb-2">Call Us</h3>
              <p className="opacity-90">020 220 1616</p>
              <p className="text-sm text-emerald-300 mt-2">Available 24/7</p>
            </a>

            <div className="flex flex-col items-center text-center p-8 bg-emerald-800/50 backdrop-blur-sm rounded-2xl border border-emerald-700/50 hover:transform hover:scale-105 transition-transform duration-300">
              <Mail className="h-12 w-12 mb-6 text-emerald-300" />
              <h3 className="text-xl font-semibold mb-2">Email Us</h3>
              <p className="opacity-90">info@azadnori.com</p>
              <p className="text-sm text-emerald-300 mt-2">We respond within 24 hours</p>
            </div>

            <div className="flex flex-col items-center text-center p-8 bg-emerald-800/50 backdrop-blur-sm rounded-2xl border border-emerald-700/50 hover:transform hover:scale-105 transition-transform duration-300">
              <MapPin className="h-12 w-12 mb-6 text-emerald-300" />
              <h3 className="text-xl font-semibold mb-2">Visit Us</h3>
              <p className="opacity-90">Ansari Square, Shahr-e-Naw, Kabul, Afghanistan</p>
              <p className="text-sm text-emerald-300 mt-2">Mon-Sat: 10am - 7pm</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  )
}